import { Injectable } from '@angular/core';
import {
  ObjectInViewportPosition,
  SessionsVptService,
} from 'src/app/services/sessions-vpt.service';
import RBush from 'rbush';
import { fabric } from 'fabric';
import { Position } from '../../../services/items-canvas.service';
import { YCanvas } from '../wb-canvas/fabric-utils';
import { isBoardItem } from '../../common/y-session';
import { FlagsService, FLAGS } from '../../../services/flags.service';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import { BoardItemsManager } from './board-items/board-items-manager';

@Injectable({
  providedIn: 'root',
})
export class BoardFreeSpaceFinder {
  private readonly paddingBetweenApps = 32;
  private readonly verticalPaddingFromBottomToolbar = 16;
  private readonly SEARCH_TIME_LIMIT_MS = 2000;
  private readonly NEW_ITEM_MARGIN_FROM_VPT_VIDEO_TOWER_ON = 200; // To not make the screen too narrow when the VCT is showing
  private readonly NEW_ITEM_MARGIN_FROM_VPT = 600;

  private rectanglesTree = new RBush();
  private canvas?: YCanvas;
  private boundFocusAppOnInsertionIfNeeded = this.focusAppOnInsertionIfNeeded.bind(this);

  constructor(private sessionsVptService: SessionsVptService, private flagsService: FlagsService) {}

  public initService(canvas: YCanvas, boardItemsManager: BoardItemsManager) {
    if (this.flagsService.isFlagEnabled(FLAGS.INSERT_APPS_IN_FREE_SPACES)) {
      this.canvas = canvas;
      this.setUpNewlyAddedItemsFocusLogic(canvas);
    }
  }

  public destroyService() {
    this.canvas?.off('object:added', this.boundFocusAppOnInsertionIfNeeded);
  }

  public setUpNewlyAddedItemsFocusLogic(canvas: YCanvas) {
    canvas.on('object:added', this.boundFocusAppOnInsertionIfNeeded);
  }

  private async focusAppOnInsertionIfNeeded(addedEvent: fabric.IEvent<Event>) {
    const fabricObject = addedEvent.target;

    // because for some items (such as groups) when added their coords are still not set, \
    // and there is no other event we can listen to, to make sure that the coords are correctly calculated
    await new Promise((res) => {
      modifiedSetTimeout(res, 100);
    });

    if (!fabricObject || !isBoardItem(fabricObject) || !fabricObject.focusOnInsertion) {
      return;
    }

    let bottomPadding = this.verticalPaddingFromBottomToolbar;
    const bottomToolbar = document.getElementById('main-toolbar-v2');
    if (bottomToolbar) {
      bottomPadding += bottomToolbar.getBoundingClientRect().height;
    }

    // No need to pan nor zoom as the object already fully visible for the user
    if (
      fabricObject.aCoords &&
      this.sessionsVptService.getObjectToViewPortPosition({
        tl_x: fabricObject.aCoords.tl.x - this.paddingBetweenApps,
        tl_y: fabricObject.aCoords.tl.y - this.paddingBetweenApps,
        br_x: fabricObject.aCoords.br.x + this.paddingBetweenApps,
        br_y: fabricObject.aCoords.br.y + bottomPadding,
      }) === ObjectInViewportPosition.FULLY_CONTAINED
    ) {
      return;
    }

    const videoTower = document.getElementById('overlay-parent-container');
    const horizontalMarginFromVptBounds = videoTower
      ? this.NEW_ITEM_MARGIN_FROM_VPT_VIDEO_TOWER_ON
      : this.NEW_ITEM_MARGIN_FROM_VPT;
    const verticalMarginFromVptBounds = this.NEW_ITEM_MARGIN_FROM_VPT;

    this.sessionsVptService.makeCanvasFitItsContent(
      this.canvas,
      [fabricObject],
      horizontalMarginFromVptBounds,
      verticalMarginFromVptBounds,
      true,
      fabricObject.configurations.height,
    );

    fabricObject.focusOnInsertion = false;
  }

  /**
   * Finds the nearest free space to the viewport center that can fit the given dimensions
   * @returns the best coords to insert the board item in that is free and closes to the center of the VPT or the right most point of the content if non found
   */
  public getPlacementCoordsOnCanvas(
    newObjectWidth: number,
    newObjectHeight: number,
    boardId: string,
  ): Position {
    const centerCoords = this.sessionsVptService.getCenterCoords();
    const positionOnVptCenter = {
      top: centerCoords.y - newObjectHeight / 2,
      left: centerCoords.x - newObjectWidth / 2,
    };

    if (!this.canvas) {
      return positionOnVptCenter;
    }

    if (this.canvas.boardId !== boardId) {
      return positionOnVptCenter;
    }

    const objects = this.canvas.getObjects();
    if (objects.length === 0) {
      return positionOnVptCenter;
    }

    let rightMostPointOfCurrentContent = -Infinity;

    // Getting the absolute coords of objects on the canvas and  converting them to the structure needed for rBush tree structure for searching through it later.
    const rectangles = objects.map((obj) => {
      const rectangle = this.fabricObjectToAbsoluteBoundingBox(obj);
      rightMostPointOfCurrentContent = Math.max(rightMostPointOfCurrentContent, rectangle.maxX);
      return rectangle;
    });

    this.rectanglesTree.clear();
    this.rectanglesTree.load(rectangles); // O( n log n ) operation

    const closestFreeSpotInContentBounds = this.findClosestFreeSpot(
      // expanding the rectangle by paddingBetweenApps in all direction to keep a space with other rectangles
      centerCoords.x - this.paddingBetweenApps,
      centerCoords.y - this.paddingBetweenApps,
      newObjectWidth + this.paddingBetweenApps * 2,
      newObjectHeight + this.paddingBetweenApps * 2,
    );

    // because the rectangle has been expanded to the top and the left to add padding when seraching,
    // now we are contracting it to the original position
    if (closestFreeSpotInContentBounds) {
      closestFreeSpotInContentBounds.top += this.paddingBetweenApps;
      closestFreeSpotInContentBounds.left += this.paddingBetweenApps;
    }

    return (
      closestFreeSpotInContentBounds ?? {
        // If no position was found, the best position is to the right of the content
        top: centerCoords.y,
        left: rightMostPointOfCurrentContent + this.paddingBetweenApps,
      }
    );
  }

  private isFree(left: number, top: number, width: number, height: number) {
    return !this.rectanglesTree.collides({
      minX: left,
      minY: top,
      maxX: left + width,
      maxY: top + height,
    }); // O (log n) operation
  }

  private findClosestFreeSpot(
    startLeft: number,
    startTop: number,
    rectWidth: number,
    rectHeight: number,
    maxSearchRadius = 10000,
  ): Position | undefined {
    if (this.isFree(startLeft, startTop, rectWidth, rectHeight)) {
      return { left: startLeft, top: startTop };
    }

    // to exit the search once we go over the time limit
    const startTime = performance.now();

    // We are searching from a start point and expanding our search radius by 'step' until we reach 'maxSearchRadius'
    // at delta we are trying the: [right, left, top, right, top-right, bottom-right, top-left, bottom-left] neighbors of the start point that are far by delta from it
    // searching for a free rectangle in those position, if found we return
    // if not found we expand our search radius and try again
    const step = 10;

    // Eight compass directions as (dx,dy) multipliers
    const directions: [number, number][] = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ];

    // Expand outward in rings
    for (let delta = step; delta <= maxSearchRadius; delta += step) {
      const currentTime = performance.now();
      if (currentTime - startTime > this.SEARCH_TIME_LIMIT_MS) {
        return;
      }
      for (const [dx, dy] of directions) {
        const left = startLeft + dx * delta;
        const top = startTop + dy * delta;
        if (this.isFree(left, top, rectWidth, rectHeight)) {
          return { left, top };
        }
      }
    }
    // Total complexity = O( (maxSearchRadius / step) * 8 * log ( n ) ) + O ( n log n ) // for inserting objects into the search tree)
    // Where n is the number of items on the board
    // Currently assuming number of items on the board is 5k
    // => O (10k / 10  *  8 * log (5k)) + 5k * log (5k))
    // => O (1k * 8 * 15 + 5k * 15)
    // => O (120k + 75k)
    // => O (195k)
  }

  private fabricObjectToAbsoluteBoundingBox(obj: fabric.Object) {
    const matrix = obj.calcTransformMatrix(); // absolute matrix including group nesting

    const width = obj.width!;
    const height = obj.height!;

    // Define local-space corners (relative to originX/center)
    const localCorners = [
      new fabric.Point(-width / 2, -height / 2), // top-left
      new fabric.Point(width / 2, -height / 2), // top-right
      new fabric.Point(width / 2, height / 2), // bottom-right
      new fabric.Point(-width / 2, height / 2), // bottom-left
    ];

    // Apply the full transform to get world-space points
    const transformedCorners = localCorners.map((p) => fabric.util.transformPoint(p, matrix));

    // Extract the bounding box
    const xs = transformedCorners.map((p) => p.x);
    const ys = transformedCorners.map((p) => p.y);
    // Using maxY instead of top + height, and so on to handle the cases of rotated objects
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      minX,
      minY,
      maxX,
      maxY,
    };
  }
}
